import React, { useState } from 'react';
import Modal from '../ui/Modal';

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemName: string;
  itemType: 'workflow' | 'dashboard' | 'widget' | 'assignment';
  isDeleting?: boolean;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  itemType,
  isDeleting = false,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const CONFIRMATION_PHRASE = 'delete me';

  const handleConfirm = () => {
    if (confirmText.toLowerCase() === CONFIRMATION_PHRASE) {
      onConfirm();
    }
  };

  const handleClose = () => {
    setConfirmText('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" showCloseButton={false}>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-danger-100 flex items-center justify-center">
            <svg className="w-6 h-6 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Delete {itemType}</h3>
            <p className="text-sm text-neutral-500">This action cannot be undone</p>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-neutral-700 mb-2">
            You are about to delete <strong>"{itemName}"</strong>.
          </p>
          <p className="text-sm text-neutral-600">
            To confirm, please type <strong className="text-danger-600">{CONFIRMATION_PHRASE}</strong> below:
          </p>
        </div>

        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={`Type "${CONFIRMATION_PHRASE}" to confirm`}
          className="w-full px-4 py-2 border border-neutral-300 rounded-lg mb-4"
          autoFocus
        />

        <div className="flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={isDeleting}
            className="px-4 py-2 text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirmText.toLowerCase() !== CONFIRMATION_PHRASE || isDeleting}
            className="px-4 py-2 text-white bg-danger-600 hover:bg-danger-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteConfirmationModal;
